import axios from 'axios';
import { authHeaders, backendUrl, requestJson } from './http';
import { demoCampaign, demoLeaderboard } from './mockData';

const DEMO_REGISTRATION_KEY = 'lds-demo-registrations';

function readDemoRegistrations() {
  try {
    return JSON.parse(window.localStorage.getItem(DEMO_REGISTRATION_KEY) || '[]');
  } catch (err) {
    return [];
  }
}

function writeDemoRegistration({ campaignId, distance }) {
  const registrations = readDemoRegistrations().filter(
    (item) => String(item.campaign?.id) !== String(campaignId)
  );
  const next = [{ campaign: { id: Number(campaignId) }, distance: Number(distance) }, ...registrations];
  window.localStorage.setItem(DEMO_REGISTRATION_KEY, JSON.stringify(next));
  return next;
}

function mediaUrl(media) {
  const item = media?.data?.[0]?.attributes;
  const url =
    item?.formats?.small?.url ||
    item?.formats?.medium?.url ||
    item?.thumbnail?.url ||
    item?.url ||
    '';
  return url ? `${backendUrl}${url}` : '';
}

export function normalizeCampaign(item) {
  const attributes = item.attributes || item;
  return {
    id: item.id,
    name: attributes.name || '',
    description: attributes.description || '',
    registerStart: attributes.register_start,
    registerEnd: attributes.register_end,
    startDate: attributes.start_date,
    endDate: attributes.end_date,
    sportTypes: attributes.sport_type || [],
    raceDistances: attributes.race_distances || [],
    minPace: attributes.min_pace,
    maxPace: attributes.max_pace,
    bannerUrl: mediaUrl(attributes.banner),
    mapCheckpoints: attributes.map_checkpoints || null,
    mapConfig: attributes.map_config || null,
  };
}

function campaignTime(value, fallback) {
  const time = new Date(value || fallback).getTime();
  return Number.isFinite(time) ? time : new Date(fallback).getTime();
}

export function selectLatestRunningCampaign(campaigns = [], now = new Date()) {
  const currentTime = new Date(now).getTime();
  const sorted = [...campaigns].sort(
    (a, b) => campaignTime(b.startDate, 0) - campaignTime(a.startDate, 0)
  );
  const running = sorted.find((campaign) => {
    const start = campaignTime(campaign.startDate, 0);
    const end = campaignTime(campaign.endDate, '9999-12-31');
    return start <= currentTime && currentTime <= end;
  });

  return running || sorted[0] || null;
}

export async function listCampaigns() {
  try {
    const result = await requestJson('/api/run-campaigns?populate[0]=banner&sort[0]=start_date:desc');
    return (result.data || []).map(normalizeCampaign);
  } catch (err) {
    return [demoCampaign];
  }
}

export async function getLatestRunningCampaign() {
  const campaigns = await listCampaigns();
  return selectLatestRunningCampaign(campaigns);
}

export async function getCampaign(id) {
  try {
    // populate=banner for the media field; all scalar fields (incl. map_checkpoints, map_config) are returned by default
    const result = await requestJson(`/api/run-campaigns/${id}?populate[0]=banner`);
    return normalizeCampaign({ id, attributes: result.data?.attributes || {} });
  } catch (err) {
    return { ...demoCampaign, id: Number(id) || demoCampaign.id };
  }
}

export async function getCurrentUserCampaigns(user) {
  const demoRegistrations = readDemoRegistrations();
  if (!user?.jwt) return demoRegistrations;
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/get-user-campaign?populate[0]=campaign`,
      { headers: authHeaders(user) }
    );
    return result.data?.status === 'success' ? result.data.data || demoRegistrations : demoRegistrations;
  } catch (err) {
    return demoRegistrations;
  }
}

export async function registerCampaign({
  user,
  campaignId,
  distance,
  teamId = null,
  joinCode = null,
  asReserve = false,
}) {
  if (!user?.jwt) {
    writeDemoRegistration({ campaignId, distance });
    return {
      status: 'success',
      message: 'Đã đăng ký demo. Khi backend sẵn sàng, luồng này sẽ gọi API thật.',
      data: { distance: Number(distance) || 0 },
    };
  }

  const result = await axios.post(
    `${backendUrl}/api/strava-provider/register-campaign`,
    {
      campaignId: Number(campaignId),
      distance: Number(distance) || 0,
      ...(teamId ? { team_id: Number(teamId) } : {}),
      ...(joinCode ? { join_code: joinCode } : {}),
      ...(asReserve ? { as_reserve: true } : {}),
    },
    { headers: authHeaders(user) }
  );
  return result.data;
}

export async function getCampaignTeams(campaignId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams`
    );
    return result.data?.data || [];
  } catch (err) {
    return [];
  }
}

export async function getCampaignTeamLeaderboard(campaignId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/leaderboard`
    );
    return result.data?.data || [];
  } catch (err) {
    return [];
  }
}

export async function getCampaignTeamDetail(campaignId, teamId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}`
    );
    return result.data?.data || null;
  } catch (err) {
    return null;
  }
}

/**
 * Join a team (during or after campaign registration).
 * @param {object} opts
 * @param {object} opts.user          - authenticated user with jwt
 * @param {number} opts.campaignId
 * @param {number} opts.teamId
 * @param {string} [opts.joinCode]    - required for private teams
 * @param {boolean} [opts.asReserve]  - join as reserve slot
 */
export async function joinCampaignTeam({ user, campaignId, teamId, joinCode, asReserve = false }) {
  const result = await axios.post(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/join`,
    { join_code: joinCode, as_reserve: asReserve },
    { headers: authHeaders(user) }
  );
  return result.data;
}

/**
 * Switch to a different team (allowed once, before campaign start_date).
 */
export async function switchCampaignTeam({ user, campaignId, teamId, joinCode }) {
  const result = await axios.post(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/switch`,
    { join_code: joinCode },
    { headers: authHeaders(user) }
  );
  return result.data;
}

// ── My team ───────────────────────────────────────────────────────────────────

export async function getMyTeam(user, campaignId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/mine`,
      { headers: authHeaders(user) }
    );
    return result.data?.data || null;
  } catch (err) {
    return null;
  }
}

// ── Invites ───────────────────────────────────────────────────────────────────

export async function getMyInvites(user) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/me/invites`,
      { headers: authHeaders(user) }
    );
    return result.data?.data || [];
  } catch (err) {
    return [];
  }
}

export async function acceptInvite(user, inviteId) {
  const result = await axios.post(
    `${backendUrl}/api/strava-provider/me/invites/${inviteId}/accept`,
    {},
    { headers: authHeaders(user) }
  );
  return result.data;
}

export async function declineInvite(user, inviteId) {
  const result = await axios.post(
    `${backendUrl}/api/strava-provider/me/invites/${inviteId}/decline`,
    {},
    { headers: authHeaders(user) }
  );
  return result.data;
}

// ── Captain actions ───────────────────────────────────────────────────────────

/**
 * Submit a replacement request (captain/VC only).
 */
export async function submitReplacementRequest({
  user,
  campaignId,
  teamId,
  replacedMemberUserCampaignId,
  replacementUserCampaignId,
  reason,
}) {
  const result = await axios.post(
    `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/replacements`,
    {
      replaced_member_user_campaign_id: replacedMemberUserCampaignId,
      replacement_user_campaign_id: replacementUserCampaignId,
      reason,
    },
    { headers: authHeaders(user) }
  );
  return result.data;
}

// ── Relay API ─────────────────────────────────────────────────────────────────

/**
 * GET /campaigns/:id/relay/status — relay config + all team progress snapshot
 */
export async function getCampaignRelayStatus(campaignId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/relay/status`
    );
    return result.data?.data || null;
  } catch (err) {
    return null;
  }
}

/**
 * GET /campaigns/:id/relay/leaderboard — relay-specific leaderboard rows
 */
export async function getCampaignRelayLeaderboard(campaignId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/relay/leaderboard`
    );
    return result.data?.data || [];
  } catch (err) {
    return [];
  }
}

/**
 * GET /campaigns/:id/teams/:teamId/relay — full journey + member stats for one team
 */
export async function getCampaignTeamJourney(campaignId, teamId) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/campaigns/${campaignId}/teams/${teamId}/relay`
    );
    return result.data?.data || null;
  } catch (err) {
    return null;
  }
}

export async function getCampaignLeaderboard({ user, campaignId }) {
  try {
    const result = await axios.get(
      `${backendUrl}/api/strava-provider/v2/campaign/${campaignId}/get-leader-board`,
      { headers: authHeaders(user) }
    );
    return result.data?.status === 'success' ? result.data.data || demoLeaderboard : demoLeaderboard;
  } catch (err) {
    return demoLeaderboard;
  }
}
